'use client';

import { useState } from 'react';
import Image from 'next/image';
import { Box, Typography } from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import { ImageUploader } from 'components/ImageUploader';

interface ProfilePictureProps {
  url?: string | null;
  name: string;
  isOwnProfile: boolean;
  onUpload: (file: File) => Promise<void>;
}

export function ProfilePicture({ url, name, isOwnProfile, onUpload }: ProfilePictureProps) {
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = async (machineId: string, file: File) => {
    try {
      setIsUploading(true);
      await onUpload(file);
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      alert('Failed to upload profile picture. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        aspectRatio: '1',
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: 'grey.100',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {url ? (
        <>
          <Image
            src={url}
            alt={`${name}'s profile`}
            fill
            sizes="(max-width: 768px) 100vw, 50vw"
            priority
            style={{ objectFit: 'cover' }}
          />
          {isOwnProfile && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'opacity 0.2s',
                opacity: 0,
                '&:hover': {
                  opacity: 1,
                },
              }}
            >
              <PhotoCamera sx={{ mr: 1 }} />
              <Typography variant="body2">Change Photo</Typography>
            </Box>
          )}
        </>
      ) : (
        <Typography color="text.secondary" align="center" p={2}>
          {isOwnProfile ? 'Click or drag to upload profile picture' : 'No profile picture'}
        </Typography>
      )}

      {isOwnProfile && (
        <ImageUploader machineId="profile-picture" onUpload={handleUpload} disabled={isUploading} />
      )}

      {isUploading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          Uploading...
        </Box>
      )}
    </Box>
  );
}
